import React, { Dispatch, ReactNode, SetStateAction } from "react";
import {
  HEADER_ID,
  itemCheckedWithSet,
  TableBody,
  TableBulkItemHeader,
  TableBulkItemRow,
  TableColumn,
  TableHeaderItem,
  toggleAll,
  toggleItemWithSet
} from "collections/Table";
import {
  areFiltersSelected,
  NoResultsIndicator,
  ToolTip,
  useDeviceFilterValue
} from "elements";
import { generateTableHeaders } from "utilities/tableUtils";
import { TABLES } from "store/userMeta";
import { useTranslation } from "utilities/i18n";
import { Link } from "polyrouter";
import { handleNullDates, handleNullFields } from "utilities/stringUtils";
import {
  UserGroupResponseMessage,
  UserGroupSourceType
} from "generated/graphql.schema";
import { PriorityDropdown } from "./PriorityDropdown";
import { DeviceUserGroupRowData } from "./deviceUserGroupListTypes";

export const DeviceUserGroupTable: React.FC<{
  userGroups: Array<DeviceUserGroupRowData>;
  userCanEdit: boolean;
  selectedItems: Array<string>;
  setSelectedItems: Dispatch<SetStateAction<string[]>>;
  loading: boolean;
  shown: TableColumn[];
}> = ({
  userGroups,
  userCanEdit,
  selectedItems,
  setSelectedItems,
  loading,
  shown
}) => {
  const { t } = useTranslation();

  const toggleItem = toggleItemWithSet(
    userGroups.length,
    selectedItems,
    setSelectedItems
  );
  const itemChecked = itemCheckedWithSet(selectedItems);

  const [filterState, , resetFilters] = useDeviceFilterValue();

  const getTableHeaders = (column: string, title: string) => {
    if (column === TABLES.DEVICE_USER_GROUPS.COLUMNS.POLICY_PRIORITY) {
      return (
        <th className="table-header" key={column}>
          <div className="role-header-container">
            <span className="column-header">{t("POLICY_PRIORITY")}</span>
            <ToolTip iconTitle={t("POLICY_PRIORITY")} position="bottom">
              <p>{t("DEVICE_USER_GROUP_POLICY_PRIORITY_TOOLTIP")}</p>
            </ToolTip>
          </div>
        </th>
      );
    }

    return <TableHeaderItem fieldName={column} title={title} key={column} />;
  };

  // add one column for checkbox if user can edit
  const columnCount = shown.length + (userCanEdit ? 1 : 0);

  return (
    <div className="table-container">
      <table className="table custom-table table-filterable">
        <TableBulkItemHeader
          showChecks={userCanEdit}
          checked={itemChecked(HEADER_ID)}
          onCheck={() =>
            toggleAll(
              userGroups.map(group => group.id),
              selectedItems,
              setSelectedItems
            )
          }
          onClearFilters={resetFilters}
          showClearFilters={areFiltersSelected(filterState)}
        >
          {generateTableHeaders(shown, getTableHeaders)}
        </TableBulkItemHeader>
        <TableBody loading={loading} cols={columnCount} accommodateScrollbar>
          {userGroups.map((group, i) => {
            return (
              <UserGroupRow
                data={group}
                key={group.id}
                onCheck={() => toggleItem(group.id)}
                checked={itemChecked(group.id)}
                showChecks={userCanEdit}
                shownColumns={shown.map(column => column.key)}
              />
            );
          })}
        </TableBody>
      </table>
      {!loading && !userGroups.length ? (
        <NoResultsIndicator title={t("NO_USER_GROUPS")} />
      ) : null}
    </div>
  );
};

type UserGroupRowProps = {
  onCheck: (id: string) => void;
  checked: boolean;
  shownColumns: Array<string>;
  showChecks: boolean;
  data: DeviceUserGroupRowData;
};

export const UserGroupRow: React.FC<UserGroupRowProps> = ({
  data,
  checked,
  showChecks,
  onCheck,
  shownColumns
}) => {
  const { t } = useTranslation();
  const { COLUMNS } = TABLES.DEVICE_USER_GROUPS;

  const getTableContent = (column: string) => {
    let content: ReactNode = null;
    switch (column) {
      case COLUMNS.NAME:
        content = (
          <Link
            nest
            to={{
              pathname: `/details/${data.id}`
            }}
          >
            {handleNullFields(data.name)}
          </Link>
        );
        break;
      case COLUMNS.USER_COUNT:
        content = handleNullFields(`${data.user_count}`);
        if (data.message === UserGroupResponseMessage.NoMembershipPermissions) {
          content = (
            <ToolTip
              icon="eye_slash"
              iconTitle={t("NO_MEMBERSHIP_PERMISSIONS")}
              position="bottom"
            >
              <span>{t("NO_MEMBERSHIP_PERMISSIONS_INFO")}</span>
            </ToolTip>
          );
        }
        break;
      case COLUMNS.LAST_MODIFIED:
        content = handleNullDates(data.updatedAt);
        break;
      case COLUMNS.CREATED_AT:
        content = handleNullDates(data.createdAt);
        break;
      case COLUMNS.GROUP_TYPE:
        content =
          data.source === UserGroupSourceType.Lens
            ? t("MANUAL")
            : t("IMPORTED");
        break;
      case COLUMNS.POLICY_PRIORITY:
        content = <PriorityDropdown policies={data.policies ?? []} />;
        break;
    }

    return <td key={column}>{content}</td>;
  };
  return (
    <TableBulkItemRow
      checked={checked}
      onCheck={() => onCheck(data.id)}
      showChecks={showChecks}
    >
      {shownColumns.map(getTableContent)}
    </TableBulkItemRow>
  );
};
